import FileSaver from 'file-saver';
import Papa from 'papaparse';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import {
  offerPresentationTargetMapping,
  PlayTargetType,
} from '@bbkAdminComponents/play/play-context';
import {
  exportExperiencePerformanceCsvForPlays,
  exportOfferPerformanceCsvForPlays,
  type BulkPlayExportContext,
  type BulkPlayExportSettings,
  type ExportCustomersData,
} from '@bbkAdminComponents/unified-plays/reporting/reports/cancel-page/cancel-page-bulk-pivot-export';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { DEFAULT_BRAND_ID } from '@bbkAdminRedux/rtkq/brands.slice';
import { type Play } from '@bbkAdminRedux/rtkq/play.slice';
import { pricingPagesSlice } from '@bbkAdminRedux/rtkq/pricing-pages.slice';
import {
  reportsSlice,
  ReportType,
  type GetReportV2Args,
  type PricingPageReportByPlay,
  type PricingPageReportByPlayDataPoint,
  type ReportFilters,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { type EnumLike } from '@bbkAdminUtils/utility-types';
import {
  dataToCsvString,
  type CsvExportField,
} from '@bbkShared/utils/export-file';

export type {
  BulkPlayExportContext,
  BulkPlayExportSettings,
  ExportCustomersData,
};
export type { BulkPlayExportContext as BulkPlayExportRuntimeContext };

export const getUnifiedPlayExportTargetType = (
  play: Play
): EnumLike<typeof PlayTargetType> =>
  offerPresentationTargetMapping[play.presentation] ?? PlayTargetType.OFFER;

export const buildUnifiedPlayExportFilters = (plays: Play[]): ReportFilters =>
  plays.map((play) => [
    { field: 'play_id', operator: 'equals' as const, value: play.id },
    {
      field: 'target_type',
      operator: 'equals' as const,
      value: getUnifiedPlayExportTargetType(play),
    },
  ]);

export const groupPlaysByExportTargetType = (
  plays: Play[]
): Map<EnumLike<typeof PlayTargetType>, Play[]> => {
  const groups = new Map<EnumLike<typeof PlayTargetType>, Play[]>();

  for (const play of plays) {
    const targetType = getUnifiedPlayExportTargetType(play);
    const existing = groups.get(targetType) ?? [];
    groups.set(targetType, [...existing, play]);
  }

  return groups;
};

export const hasMixedExportTargetTypes = (plays: Play[]): boolean =>
  groupPlaysByExportTargetType(plays).size > 1;

/** Cancel-page pivot exports require Starter Premium, matching individual report export. */
export const bulkExportRequiresStarterPremium = (plays: Play[]): boolean =>
  groupPlaysByExportTargetType(plays).has(PlayTargetType.CANCEL_PAGE);

const EXPORT_TARGET_TYPE_FILE_LABELS: Record<
  EnumLike<typeof PlayTargetType>,
  string
> = {
  [PlayTargetType.OFFER]: 'in-app-offers',
  [PlayTargetType.HOSTED_PAGE]: 'hosted-page',
  [PlayTargetType.CANCEL_PAGE]: 'cancel-page',
  [PlayTargetType.PRICING_PAGE]: 'pricing-page',
  [PlayTargetType.NONE]: 'other',
};

export const getExportTargetTypeFileLabel = (
  targetType: EnumLike<typeof PlayTargetType>
): string => EXPORT_TARGET_TYPE_FILE_LABELS[targetType] ?? targetType;

type BulkExportSheet = {
  fileName: string;
  kind: 'server' | 'client';
  reportType?: ReportType;
  buildServerFilters?: (plays: Play[]) => ReportFilters;
  fetchClientCsv?: (
    context: BulkPlayExportContext,
    plays: Play[],
    pricingRowsCache?: Map<string, PricingPageReportByPlayDataPoint[]>,
    pricingPageNamesCache?: Map<string, Record<string, string>>
  ) => Promise<string>;
};

type BulkExportTargetConfig = {
  sheets: BulkExportSheet[];
};

/** Matches LifecycleCustomerSnapshot field order in the lifecycle entity export serializer. */
const LIFECYCLE_ENTITY_COLUMNS: CsvExportField[] = [
  { value: 'customer_name', label: 'customer_name' },
  { value: 'play_name', label: 'Play Name' },
  { value: 'offer_name', label: 'offer_name' },
  { value: 'offer_id', label: 'offer_id' },
  { value: 'customer_id', label: 'customer_id' },
  { value: 'customer_email', label: 'customer_email' },
  { value: 'last_engaged_date', label: 'last_engaged_date' },
  { value: 'user_id', label: 'user_id' },
  { value: 'last_engaged_status', label: 'last_engaged_status' },
  { value: 'fulfillment_status', label: 'fulfillment_status' },
  { value: 'revenue', label: 'revenue' },
  { value: 'checkout_started', label: 'checkout_started' },
  { value: 'checkout_completed', label: 'checkout_completed' },
];

const PLAY_ID_FIELD = 'play_id';
const PLAY_ID_LABEL = 'Play ID';
const PLAY_NAME_FIELD = 'play_name';
const PLAY_NAME_LABEL = 'Play Name';

const buildPlayNamesById = (plays: Play[]): Record<string, string> =>
  Object.fromEntries(plays.map((play) => [play.id, play.name]));

const normalizeExportFieldLabel = (field: string): string =>
  field === PLAY_NAME_FIELD ? PLAY_NAME_LABEL : field;

const toHeaderOnlyCsv = (fields: string[]): string =>
  dataToCsvString(
    [],
    fields.map((field) => ({
      value: field,
      label: normalizeExportFieldLabel(field),
    }))
  );

const resolvePlayExportColumnFields = (
  baseColumns: CsvExportField[],
  includePlayId: boolean
): CsvExportField[] => {
  const withoutPlayId = includePlayId
    ? baseColumns
    : baseColumns.filter((column) => column.value !== PLAY_ID_FIELD);

  const dataColumns = withoutPlayId.filter(
    (column) => column.value !== PLAY_NAME_FIELD
  );

  const playIdIndex = dataColumns.findIndex(
    (column) => column.value === PLAY_ID_FIELD
  );
  const playNameColumn: CsvExportField = {
    value: PLAY_NAME_FIELD,
    label: PLAY_NAME_LABEL,
  };

  if (playIdIndex === -1) {
    return [playNameColumn, ...dataColumns];
  }

  return [
    ...dataColumns.slice(0, playIdIndex + 1),
    playNameColumn,
    ...dataColumns.slice(playIdIndex + 1),
  ];
};

/** Ensures bulk server CSV exports include a Play Name column with row values. */
export const enrichServerExportCsvWithPlayColumns = (
  csv: string,
  plays: Play[]
): string => {
  const trimmed = csv.trim();
  if (!trimmed) {
    return csv;
  }

  const parsed = Papa.parse<Record<string, string>>(trimmed, {
    header: true,
    skipEmptyLines: true,
  });

  if (!parsed.data.length) {
    const headerFields = parsed.meta.fields ?? [];
    return headerFields.length ? toHeaderOnlyCsv(headerFields) : csv;
  }

  const playNamesById = buildPlayNamesById(plays);
  const includePlayId = plays.length > 1;
  const singlePlayName = plays.length === 1 ? plays[0]!.name : undefined;
  const originalFields =
    parsed.meta.fields ?? Object.keys(parsed.data[0] ?? {});
  const dataFields = originalFields.filter(
    (field) =>
      field !== PLAY_NAME_FIELD &&
      field !== PLAY_NAME_LABEL &&
      (includePlayId || field !== PLAY_ID_FIELD)
  );

  const rows = parsed.data.map((row) => {
    const resolvedPlayName =
      row[PLAY_NAME_LABEL] ||
      row[PLAY_NAME_FIELD] ||
      (row[PLAY_ID_FIELD] ? playNamesById[row[PLAY_ID_FIELD]!] : undefined) ||
      singlePlayName ||
      '';

    return {
      ...(includePlayId && row[PLAY_ID_FIELD]
        ? { [PLAY_ID_FIELD]: row[PLAY_ID_FIELD] }
        : {}),
      [PLAY_NAME_FIELD]: resolvedPlayName,
      ...Object.fromEntries(
        dataFields.map((field) => [field, row[field] ?? ''])
      ),
    };
  });

  const fields: CsvExportField[] = [];
  if (includePlayId) {
    fields.push({ value: PLAY_ID_FIELD, label: PLAY_ID_LABEL });
  }
  fields.push({ value: PLAY_NAME_FIELD, label: PLAY_NAME_LABEL });
  for (const field of dataFields) {
    if (field !== PLAY_ID_FIELD) {
      fields.push({ value: field, label: field });
    }
  }

  return dataToCsvString(rows, fields);
};

const fetchLifecycleServerExportCsv = async (
  context: BulkPlayExportContext,
  sheet: BulkExportSheet,
  plays: Play[]
): Promise<string> => {
  if (!sheet.reportType || !sheet.buildServerFilters) {
    throw new Error('Server export sheet is missing report configuration');
  }

  const downloadUrl = await context.exportLCOReportingData(
    {
      start: context.start,
      end: context.end,
      format: 'CSV',
      reportType: sheet.reportType,
      filters: sheet.buildServerFilters(plays),
      ...(plays.length > 1 && { orFilters: true }),
    },
    {
      appId: context.appId,
      companyId: context.companyId,
      returnDownloadUrl: true,
      suppressNotifications: true,
    }
  );

  if (!downloadUrl) {
    throw new Error('Export did not return a download URL');
  }

  const response = await fetch(downloadUrl);
  if (!response.ok) {
    throw new Error(`Failed to download ${sheet.fileName}`);
  }

  const csv = await response.text();
  if (!csv.trim()) {
    return dataToCsvString([], LIFECYCLE_ENTITY_COLUMNS);
  }

  return enrichServerExportCsvWithPlayColumns(csv, plays);
};

/** Matches pricing table report rate formatting in play-pricing-table-reports.tsx */
const formatPricingRate = (
  numerator: unknown,
  denominator: unknown
): string => {
  const toNum = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  };
  const num = toNum(numerator);
  const den = toNum(denominator);
  const value = den > 0 ? (num / den) * 100 : 0;
  return Number.isFinite(value) ? value.toFixed(2) : '0.00';
};

const PRICING_USER_COLUMNS: CsvExportField[] = [
  { value: 'play_id', label: 'Play ID' },
  { value: 'play_name', label: 'Play Name' },
  { value: 'pricing_table', label: 'Pricing table' },
  { value: 'customers_entered', label: 'Customers Entered' },
  { value: 'customers_engaged', label: 'Customers Engaged' },
  { value: 'customer_click_through_rate', label: 'Click Through Rate' },
  { value: 'checkouts_started', label: 'Checkouts Started' },
  { value: 'checkouts_completed', label: 'Checkouts Completed' },
  { value: 'customer_checkout_rate', label: 'Checkout Rate' },
  {
    value: 'overall_customer_conversion_rate',
    label: 'Overall Conversion Rate',
  },
];

const PRICING_SESSION_COLUMNS: CsvExportField[] = [
  { value: 'play_id', label: 'Play ID' },
  { value: 'play_name', label: 'Play Name' },
  { value: 'pricing_table', label: 'Pricing table' },
  { value: 'sessions_rendered', label: 'Sessions Rendered' },
  { value: 'sessions_engaged', label: 'Sessions w/ engagement' },
  { value: 'session_click_through_rate', label: 'Click Through Rate' },
  { value: 'sessions_checkouts_started', label: 'Sessions Checkouts Started' },
  {
    value: 'sessions_checkouts_completed',
    label: 'Sessions Checkouts Completed',
  },
  { value: 'session_checkout_rate', label: 'Checkout Rate' },
  { value: 'session_conversion_rate', label: 'Overall Conversion Rate' },
];

const enrichPricingRows = (
  rows: PricingPageReportByPlayDataPoint[],
  pricingPageNamesById: Record<string, string>,
  playNamesById: Record<string, string>
): Record<string, unknown>[] =>
  rows.map((row) => ({
    play_id: row.play_id,
    play_name: playNamesById[row.play_id] ?? '',
    pricing_table: pricingPageNamesById[row.target_id] ?? row.target_id,
    customers_entered: row.customers_entered,
    customers_engaged: row.customers_engaged,
    checkouts_started: row.checkouts_started,
    checkouts_completed: row.checkouts_completed,
    sessions_rendered: row.sessions_rendered,
    sessions_engaged: row.sessions_engaged,
    sessions_checkouts_started: row.sessions_checkouts_started,
    sessions_checkouts_completed: row.sessions_checkouts_completed,
    customer_click_through_rate: formatPricingRate(
      row.customers_engaged,
      row.customers_entered
    ),
    customer_checkout_rate: formatPricingRate(
      row.checkouts_completed,
      row.checkouts_started
    ),
    overall_customer_conversion_rate: formatPricingRate(
      row.checkouts_completed,
      row.customers_entered
    ),
    session_click_through_rate: formatPricingRate(
      row.sessions_engaged,
      row.sessions_rendered
    ),
    session_checkout_rate: formatPricingRate(
      row.sessions_checkouts_completed,
      row.sessions_checkouts_started
    ),
    session_conversion_rate: formatPricingRate(
      row.sessions_checkouts_completed,
      row.sessions_rendered
    ),
  }));

const fetchReportV2 = async <T>(
  context: BulkPlayExportContext,
  args: Omit<GetReportV2Args, 'appKey'>
): Promise<T> =>
  context
    .dispatch(
      reportsSlice.endpoints.getReportV2.initiate({
        appKey: context.appId,
        ...args,
      })
    )
    .unwrap() as Promise<T>;

const fetchPricingPageNames = async (
  context: BulkPlayExportContext,
  brandId: string
): Promise<Record<string, string>> => {
  const pricingPages = await context
    .dispatch(
      pricingPagesSlice.endpoints.getPricingPagesList.initiate({
        appKey: context.appId,
        brand_id: brandId,
        context: context.isDemoData
          ? BBKRequestContext.lifecycle_reports
          : undefined,
      })
    )
    .unwrap();

  return Object.fromEntries(
    pricingPages.map((page) => [page.id, page.name ?? page.id])
  );
};

const fetchPricingRowsForPlays = async (
  context: BulkPlayExportContext,
  plays: Play[]
): Promise<PricingPageReportByPlayDataPoint[]> => {
  const report = await fetchReportV2<PricingPageReportByPlay>(context, {
    start: context.start,
    end: context.end,
    context: context.isDemoData
      ? BBKRequestContext.lifecycle_reports
      : undefined,
    report_type_requests: [
      { report_type: ReportType.pricing_page_report_by_play },
    ],
  });

  const playIds = new Set(plays.map((play) => play.id));
  return (report.data ?? []).filter((row) => playIds.has(row.play_id));
};

const fetchPricingRowsCached = async (
  context: BulkPlayExportContext,
  plays: Play[],
  cache: Map<string, PricingPageReportByPlayDataPoint[]>
): Promise<PricingPageReportByPlayDataPoint[]> => {
  const cacheKey = plays
    .map((play) => play.id)
    .sort()
    .join(',');
  const cached = cache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const rows = await fetchPricingRowsForPlays(context, plays);
  cache.set(cacheKey, rows);
  return rows;
};

const buildPricingExportCsv = async (
  context: BulkPlayExportContext,
  plays: Play[],
  columns: CsvExportField[],
  pricingRowsCache?: Map<string, PricingPageReportByPlayDataPoint[]>,
  pricingPageNamesCache?: Map<string, Record<string, string>>
): Promise<string> => {
  const rows = pricingRowsCache
    ? await fetchPricingRowsCached(context, plays, pricingRowsCache)
    : await fetchPricingRowsForPlays(context, plays);

  const pricingPageNamesById: Record<string, string> = {};
  const brandIds = [
    ...new Set(plays.map((play) => play.brand_id ?? DEFAULT_BRAND_ID)),
  ];

  for (const brandId of brandIds) {
    let names = pricingPageNamesCache?.get(brandId);
    if (!names) {
      names = await fetchPricingPageNames(context, brandId);
      pricingPageNamesCache?.set(brandId, names);
    }
    Object.assign(pricingPageNamesById, names);
  }

  const playNamesById = buildPlayNamesById(plays);
  const includePlayId = plays.length > 1;
  const exportColumns = resolvePlayExportColumnFields(columns, includePlayId);

  return dataToCsvString(
    enrichPricingRows(rows, pricingPageNamesById, playNamesById),
    exportColumns
  );
};

const BULK_EXPORT_TARGET_CONFIG: Record<
  EnumLike<typeof PlayTargetType>,
  BulkExportTargetConfig
> = {
  [PlayTargetType.OFFER]: {
    sheets: [
      {
        fileName: 'customer-level-report.csv',
        kind: 'server',
        reportType:
          ReportType.lifecycle_entity_metrics_customer_snapshot_export,
        buildServerFilters: buildUnifiedPlayExportFilters,
      },
      {
        fileName: 'user-level-report.csv',
        kind: 'server',
        reportType: ReportType.lifecycle_entity_metrics_user_export,
        buildServerFilters: buildUnifiedPlayExportFilters,
      },
    ],
  },
  [PlayTargetType.HOSTED_PAGE]: {
    sheets: [
      {
        fileName: 'customer-level-report.csv',
        kind: 'server',
        reportType:
          ReportType.lifecycle_entity_metrics_customer_snapshot_export,
        buildServerFilters: buildUnifiedPlayExportFilters,
      },
      {
        fileName: 'user-level-report.csv',
        kind: 'server',
        reportType: ReportType.lifecycle_entity_metrics_user_export,
        buildServerFilters: buildUnifiedPlayExportFilters,
      },
    ],
  },
  [PlayTargetType.CANCEL_PAGE]: {
    sheets: [
      {
        fileName: 'experience-performance-report.csv',
        kind: 'client',
        fetchClientCsv: (context, plays) =>
          exportExperiencePerformanceCsvForPlays(context, plays),
      },
      {
        fileName: 'offer-performance-report.csv',
        kind: 'client',
        fetchClientCsv: (context, plays) =>
          exportOfferPerformanceCsvForPlays(context, plays),
      },
    ],
  },
  [PlayTargetType.PRICING_PAGE]: {
    sheets: [
      {
        fileName: 'user-level-sessions-report.csv',
        kind: 'client',
        fetchClientCsv: (
          context,
          plays,
          pricingRowsCache,
          pricingPageNamesCache
        ) =>
          buildPricingExportCsv(
            context,
            plays,
            PRICING_USER_COLUMNS,
            pricingRowsCache,
            pricingPageNamesCache
          ),
      },
      {
        fileName: 'visitor-level-sessions-report.csv',
        kind: 'client',
        fetchClientCsv: (
          context,
          plays,
          pricingRowsCache,
          pricingPageNamesCache
        ) =>
          buildPricingExportCsv(
            context,
            plays,
            PRICING_SESSION_COLUMNS,
            pricingRowsCache,
            pricingPageNamesCache
          ),
      },
    ],
  },
  [PlayTargetType.NONE]: {
    sheets: [],
  },
};

export const isPlayBulkExportEligible = (play: Play): boolean =>
  (BULK_EXPORT_TARGET_CONFIG[getUnifiedPlayExportTargetType(play)]?.sheets
    .length ?? 0) > 0;

export const filterBulkExportEligiblePlays = (plays: Play[]): Play[] =>
  plays.filter(isPlayBulkExportEligible);

/** Bulk export requires every selected play to support export. */
export const isBulkExportSelectionEligible = (plays: Play[]): boolean =>
  plays.length > 0 && plays.every(isPlayBulkExportEligible);

export const BULK_EXPORT_INELIGIBLE_SELECTION_TOOLTIP =
  'Export is only available when all selected plays support export.';

type ZipFileEntry = {
  path: string;
  content: string;
};

const makeCrc32Table = (): Uint32Array => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let crc = i;
    for (let j = 0; j < 8; j++) {
      crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
    }
    table[i] = crc >>> 0;
  }
  return table;
};

const CRC32_TABLE = makeCrc32Table();

const crc32 = (data: Uint8Array): number => {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc = CRC32_TABLE[(crc ^ data[i]!) & 0xff]! ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

/** Builds a ZIP archive with stored CSV entries for bulk play export. */
const createZipBlob = (entries: ZipFileEntry[]): Blob => {
  const encoder = new TextEncoder();
  const parts: Uint8Array[] = [];
  const centralDirectory: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const pathBytes = encoder.encode(entry.path);
    const data = encoder.encode(entry.content);
    const checksum = crc32(data);

    const localHeader = new Uint8Array(30 + pathBytes.length);
    const localView = new DataView(localHeader.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(8, 0, true);
    localView.setUint32(14, checksum, true);
    localView.setUint32(18, data.length, true);
    localView.setUint32(22, data.length, true);
    localView.setUint16(26, pathBytes.length, true);
    localHeader.set(pathBytes, 30);

    const localHeaderOffset = offset;
    parts.push(localHeader, data);
    offset += localHeader.length + data.length;

    const centralHeader = new Uint8Array(46 + pathBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint32(16, checksum, true);
    centralView.setUint32(20, data.length, true);
    centralView.setUint32(24, data.length, true);
    centralView.setUint16(28, pathBytes.length, true);
    centralView.setUint32(42, localHeaderOffset, true);
    centralHeader.set(pathBytes, 46);
    centralDirectory.push(centralHeader);
  }

  const centralDirectoryOffset = offset;
  for (const header of centralDirectory) {
    parts.push(header);
  }

  const centralDirectorySize = centralDirectory.reduce(
    (size, header) => size + header.length,
    0
  );

  const endRecord = new Uint8Array(22);
  const endView = new DataView(endRecord.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(8, entries.length, true);
  endView.setUint16(10, entries.length, true);
  endView.setUint32(12, centralDirectorySize, true);
  endView.setUint32(16, centralDirectoryOffset, true);
  parts.push(endRecord);

  return new Blob(parts as BlobPart[], { type: 'application/zip' });
};

const getZipEntryPath = (
  targetType: EnumLike<typeof PlayTargetType>,
  fileName: string,
  isMixed: boolean
): string => {
  if (!isMixed) {
    return fileName;
  }
  return `${getExportTargetTypeFileLabel(targetType)}/${fileName}`;
};

export const buildBulkPlayZipEntries = async (
  plays: Play[],
  context: BulkPlayExportContext
): Promise<ZipFileEntry[]> => {
  const groups = groupPlaysByExportTargetType(plays);
  const isMixed = groups.size > 1;
  const zipEntries: ZipFileEntry[] = [];
  const pricingRowsCache = new Map<
    string,
    PricingPageReportByPlayDataPoint[]
  >();
  const pricingPageNamesCache = new Map<string, Record<string, string>>();

  for (const [targetType, groupPlays] of groups) {
    const config = BULK_EXPORT_TARGET_CONFIG[targetType];
    if (!config?.sheets.length) {
      continue;
    }

    for (const sheet of config.sheets) {
      const csv =
        sheet.kind === 'server'
          ? await fetchLifecycleServerExportCsv(context, sheet, groupPlays)
          : await sheet.fetchClientCsv!(
              context,
              groupPlays,
              pricingRowsCache,
              pricingPageNamesCache
            );

      zipEntries.push({
        path: getZipEntryPath(targetType, sheet.fileName, isMixed),
        content: csv,
      });
    }
  }

  return zipEntries;
};

export const getBulkPlayZipFileName = (plays: Play[]): string => {
  const isMixed = groupPlaysByExportTargetType(plays).size > 1;
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

  if (isMixed) {
    return `plays-export-${timestamp}.zip`;
  }

  return `${getExportTargetTypeFileLabel(
    getUnifiedPlayExportTargetType(plays[0]!)
  )}-export-${timestamp}.zip`;
};

export const exportBulkPlaysAsZip = async (
  plays: Play[],
  context: BulkPlayExportContext
): Promise<void> => {
  if (!isBulkExportSelectionEligible(plays)) {
    BlackToastTrigger({
      content: BULK_EXPORT_INELIGIBLE_SELECTION_TOOLTIP,
      options: { autoClose: 6000 },
    });
    return;
  }

  if (context.isStarterFreePlan && bulkExportRequiresStarterPremium(plays)) {
    BlackToastTrigger({
      content: 'Export requires Starter Premium plan or higher.',
      options: { autoClose: 6000 },
    });
    return;
  }

  BlackToastTrigger({
    content: 'Export being generated. Please wait.',
    options: { autoClose: 3000 },
  });

  try {
    const zipEntries = await buildBulkPlayZipEntries(plays, context);

    if (!zipEntries.length) {
      throw new Error('No export data available for the selected plays');
    }

    const zipBlob = createZipBlob(zipEntries);
    FileSaver.saveAs(zipBlob, getBulkPlayZipFileName(plays));

    BlackToastTrigger({
      content: 'Export successfully generated.',
    });
  } catch (error) {
    BlackToastTrigger({
      content: 'An error occurred during export, please try again.',
      options: { autoClose: 6000 },
    });
    throw error;
  }
};

export const getBulkExportSheetCount = (plays: Play[]): number => {
  if (!isBulkExportSelectionEligible(plays)) {
    return 0;
  }

  const groups = groupPlaysByExportTargetType(plays);
  let count = 0;
  for (const [targetType] of groups) {
    count += BULK_EXPORT_TARGET_CONFIG[targetType]?.sheets.length ?? 0;
  }
  return count;
};
